import { createDefaultCoordinates, type Coordinates } from "$lib/features/_other/coordinates.type";
import { snapToNearestMapCopy } from "$lib/utils/geo.util";
import type { MapLayerMouseEvent } from "maplibre-gl";

export type PopupOpenOrigin = 'click' | 'hover';

export interface PopupProps<T extends { id: string }> {
    open: {
        isOpened: boolean;
        origin: PopupOpenOrigin | undefined;
    };
    coordinates: Coordinates;
    props: T | undefined;
}

export class PopupController<T extends { id: string }> {
    private _popup = $state<PopupProps<T>>({
        open: {
            isOpened: false,
            origin: undefined
        },
        coordinates: createDefaultCoordinates(),
        props: undefined
    });

    private closeTimeout: ReturnType<typeof setTimeout> | null = null;

    public get popup() {
        return this._popup;
    }

    public openPopup(originOpenedWith: PopupOpenOrigin, props: T, coordinates: Coordinates): void {
        this.clearCloseTimeout();

        // If currently click-locked, only allow a new 'click' to override it
        if (this._popup.open.isOpened && this._popup.open.origin === 'click') {
            if (originOpenedWith === 'hover') return; // Ignore hover requests while locked
            if (this._popup.props?.id === props.id) return; // Ignore click on the same active marker
        }

        this._popup = {
            open: { isOpened: true, origin: originOpenedWith },
            coordinates,
            props
        };
    }

    public closePopup(originToClose: PopupOpenOrigin | undefined, { doAfter = undefined }: { doAfter?: () => void } = {}): void {
        // Ignore hover-close requests if the popup is locked via click
        if (this._popup.open.origin === 'click' && originToClose === 'hover') {
            return;
        }

        const performClose = () => {
            this._popup = {
                open: { isOpened: false, origin: undefined },
                coordinates: createDefaultCoordinates(),
                props: undefined
            };
            if (doAfter) doAfter();
        };

        if (originToClose === 'hover') {
            // Delay the hover closure to let the user move their mouse into the popup
            this.clearCloseTimeout();
            this.closeTimeout = setTimeout(() => {
                performClose();
            }, 150);
        } else {
            // Instant closure for manual or click closes
            this.clearCloseTimeout();
            performClose();
        }
    }

    // Callers bound to the Svelte <Popup> HTML container wrapper
    public onPopupMouseEnter(): void {
        this.clearCloseTimeout(); // Keep open while hovering inside the popup itself
    }

    public onPopupMouseLeave(): void {
        this.closePopup('hover'); // Close when leaving the popup
    }

    private clearCloseTimeout() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
    }

    // Maplibre Layer Event Handlers
    public onMouseMove(e: MapLayerMouseEvent): void {
        const feature = e.features?.[0];
        if (!feature || feature.geometry.type !== 'Point') {
            if (this._popup.open.origin === 'hover') {
                this.closePopup('hover');
            }
            e.target.getCanvas().style.cursor = '';
            return;
        }

        e.target.getCanvas().style.cursor = 'pointer';
        const props = feature.properties as T;

        const isPopupClosed = !this._popup.open.isOpened;
        const isDifferentFeature = this._popup.props?.id !== props.id;

        if (isPopupClosed || isDifferentFeature) {
            const featureCoordinates: Coordinates = {
                longitude: feature.geometry.coordinates[0],
                latitude: feature.geometry.coordinates[1]
            };
            const coordinates: Coordinates = snapToNearestMapCopy(featureCoordinates, { longitude: e.lngLat.lng, latitude: e.lngLat.lat });
            this.openPopup('hover', props, coordinates);
        } else {
            this.clearCloseTimeout(); // Hovered back onto the same marker: cancel any close timeout
        }
    }

    public onMouseLeave(e: MapLayerMouseEvent): void {
        e.target.getCanvas().style.cursor = '';
        this.closePopup('hover');
    }
}